import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static values = {
    placeholder: { type: String, default: "Select..." },
    allowEmpty: { type: Boolean, default: false },
    maxOptions: { type: Number, default: 50 },
    dropdownParent: { type: String, default: "" },
    dropdownInput: { type: Boolean, default: true },
    tree: { type: Boolean, default: false },
    // Keeps the chosen option on one line, as a row in a table has to be, but
    // carries the detail it used to truncate away in a tooltip on hover.
    rich: { type: Boolean, default: false }
  }

  connect() {
    this.initTomSelect()
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  initTomSelect() {
    const isMultiple = this.element.multiple
    const plugins = []

    if (this.dropdownInputValue) {
      plugins.push('dropdown_input')
    }

    if (isMultiple) {
      plugins.push('remove_button')
    }

    const options = {
      plugins,
      maxOptions: this.maxOptionsValue,
      placeholder: this.placeholderValue,
      allowEmptyOption: this.allowEmptyValue,
      create: false,
      persist: false,
      openOnFocus: true,
      highlight: true,
      closeAfterSelect: !isMultiple,
      hideSelected: false,
      // Custom render for compact text-xs styling
      render: {
        option: (data, escape) => {
          if (this.treeValue) {
            return this.renderTreeOption(data, escape)
          }

          return this.renderDetailed(data, escape, "option")
        },
        item: (data, escape) => {
          if (this.treeValue) {
            const path = data.treePath || data.tree_path || data.text
            return `<div class="item">${escape(path)}</div>`
          }

          const text = data.secondary
            ? `${data.primary || data.text} | ${data.secondary}`
            : data.primary || data.text

          if (!this.richValue) {
            return `<div class="item">${escape(text)}</div>`
          }

          // One line in the control, because the picker sits in a table row.
          // The photo and the rest slide out under the cursor instead.
          return `<div class="item ts-rich-item">
            <span class="ts-rich-line">${escape(text)}</span>
            ${this.renderHoverCard(data, escape)}
          </div>`
        },
        optgroup_header: (data, escape) => {
          return `<div class="optgroup-header">${escape(data.label)}</div>`
        },
        no_results: () => {
          return `<div class="no-results">${this.noResultsText()}</div>`
        }
      },
      onInitialize: () => {
        // Add dark mode classes to the wrapper
        this.applyDarkModeStyles()
      },
      onDropdownOpen: () => {
        const scrollX = window.scrollX
        const scrollY = window.scrollY
        if (this.dropdownParentValue === "body") {
          this.positionDropdownToBody()
          this.startBodyDropdownTracking()
        }
        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY)
        })
      },
      onDropdownClose: () => {
        this.stopBodyDropdownTracking()
      },
      onChange: (value) => {
        // Dispatch change event for other controllers to listen
        this.element.dispatchEvent(new CustomEvent('change', { bubbles: true }))
      }
    }

    if (this.dropdownParentValue) {
      options.dropdownParent = this.dropdownParentValue === "body"
        ? document.body
        : document.querySelector(this.dropdownParentValue)
    }

    if (this.treeValue) {
      options.searchField = ["text", "treePath", "tree_path"]
    }

    this.tomSelect = new TomSelect(this.element, options)
    if (this.dropdownParentValue === "body") {
      // Tom Select only positions a body dropdown when given the string
      // "body", and open() focuses the search input right after positioning.
      // Place it ourselves at that moment, or the browser scrolls the page to
      // an unplaced dropdown near the top of the document.
      this.tomSelect.positionDropdown = () => this.positionDropdownToBody()
    }
    if (!this.treeValue) this.hideDisabledOptions()
  }

  // What the single line has to leave out: the photo, the attributes and the
  // fitment. Shown on hover so the control stays one row tall.
  renderHoverCard(data, escape) {
    const photoUrl = data.photoUrl || data.photo_url
    const image = photoUrl
      ? `<img class="w-16 h-16 rounded object-cover border border-slate-200 dark:border-slate-600 flex-none" src="${escape(photoUrl)}" loading="lazy" decoding="async" alt="">`
      : ""

    const lines = [`<div class="font-medium">${escape(data.primary || data.text)}</div>`]
    if (data.secondary) {
      lines.push(`<div class="ts-option-secondary">${escape(data.secondary)}</div>`)
    }
    if (data.references) {
      lines.push(`<div class="ts-option-references">${escape(data.references)}</div>`)
    }

    return `<div class="ts-rich-card">${image}<div class="min-w-0 flex-1">${lines.join("")}</div></div>`
  }

  // The photo, both lines and the reference items. Used for the dropdown list
  // always, and for the selection itself when the picker is rich.
  renderDetailed(data, escape, className) {
    const secondary = data.secondary
    const references = data.references
    const photoUrl = data.photoUrl || data.photo_url
    const primary = data.primary || data.text

    if (!data.primary && !secondary && !photoUrl) {
      return `<div class="${className}">${escape(data.text)}</div>`
    }

    const image = photoUrl
      ? `<img class="w-9 h-9 rounded object-cover border border-slate-200 dark:border-slate-600 flex-none" src="${escape(photoUrl)}" loading="lazy" decoding="async" alt="">`
      : `<div class="w-9 h-9 rounded border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 flex-none"></div>`

    const lines = [`<div class="font-medium whitespace-normal break-words">${escape(primary)}</div>`]
    if (secondary) {
      lines.push(`<div class="ts-option-secondary text-xs whitespace-normal break-words">${escape(secondary)}</div>`)
    }
    if (references) {
      lines.push(`<div class="ts-option-references text-xs whitespace-normal break-words">${escape(references)}</div>`)
    }

    return `<div class="${className} flex items-start gap-2 leading-tight whitespace-normal">
      ${image}
      <div class="min-w-0 flex-1">${lines.join("")}</div>
    </div>`
  }

  renderTreeOption(data, escape) {
    const depth = Math.max(Number(data.treeDepth || data.tree_depth || 0), 0)
    const leafValue = data.treeLeaf ?? data.tree_leaf
    const isLeaf = leafValue === true || leafValue === "true"
    const label = data.treeLabel || data.tree_label || data.text
    const guides = Array.from(
      { length: depth },
      () => '<span class="w-3 self-stretch border-l border-slate-200 dark:border-slate-600"></span>'
    ).join("")
    const marker = isLeaf
      ? '<span class="h-1 w-1 shrink-0 rounded-full bg-teal-500"></span>'
      : `<svg class="h-3 w-3 shrink-0 text-slate-400" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`
    const labelClass = isLeaf
      ? "text-slate-700 dark:text-slate-200"
      : "font-semibold text-slate-600 dark:text-slate-300"

    return `<div class="option ts-tree-option flex items-center whitespace-normal">
      ${guides}${marker}
      <span class="${labelClass} break-words">${escape(label)}</span>
    </div>`
  }

  noResultsText() {
    return document.documentElement.lang?.toLowerCase().startsWith("uk")
      ? "Нічого не знайдено"
      : "No results found"
  }

  hideDisabledOptions() {
    const originalSearch = this.tomSelect.search.bind(this.tomSelect)

    this.tomSelect.search = (query) => {
      const results = originalSearch(query)
      return {
        ...results,
        items: results.items.filter((item) => {
          const option = this.tomSelect.options[item.id]
          return !option?.disabled
        })
      }
    }
  }

  // Fixed to the viewport, so scrolling the page cannot bring a cut-off list
  // into view. Open upwards when there is more room above, and shrink the
  // list to the room there is, so every option stays reachable.
  positionDropdownToBody() {
    if (!this.tomSelect) return

    const wrapper = this.tomSelect.wrapper
    const dropdown = this.tomSelect.dropdown
    const content = this.tomSelect.dropdown_content
    if (!wrapper || !dropdown || !content) return

    const rect = wrapper.getBoundingClientRect()
    const gap = 8
    this.listMaxHeight ??= parseFloat(getComputedStyle(content).maxHeight) || 240

    const chrome = dropdown.offsetHeight - content.offsetHeight
    const wanted = chrome + Math.min(content.scrollHeight, this.listMaxHeight)
    const below = window.innerHeight - rect.bottom - gap
    const above = rect.top - gap
    const upward = wanted > below && above > below
    const room = upward ? above : below

    content.style.maxHeight = `${Math.max(Math.min(this.listMaxHeight, room - chrome), 80)}px`
    dropdown.style.position = "fixed"
    dropdown.style.width = `${rect.width}px`
    dropdown.style.left = `${rect.left}px`
    // Anchor the edge next to the control, so the list can shrink while
    // searching without leaving a gap.
    dropdown.style.top = upward ? "auto" : `${rect.bottom}px`
    dropdown.style.bottom = upward ? `${window.innerHeight - rect.top + 2}px` : "auto"
  }

  startBodyDropdownTracking() {
    if (this.dropdownTracking) return

    this.dropdownTracking = (event) => {
      // Scrolling the option list itself must not re-measure the dropdown.
      if (event?.type === "scroll" && this.tomSelect?.dropdown?.contains(event.target)) return
      if (this.rafId) return
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null
        this.positionDropdownToBody()
      })
    }

    window.addEventListener("scroll", this.dropdownTracking, true)
    window.addEventListener("resize", this.dropdownTracking)
  }

  stopBodyDropdownTracking() {
    if (!this.dropdownTracking) return

    window.removeEventListener("scroll", this.dropdownTracking, true)
    window.removeEventListener("resize", this.dropdownTracking)
    this.dropdownTracking = null

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  applyDarkModeStyles() {
    const wrapper = this.tomSelect?.wrapper || this.element.nextElementSibling
    if (wrapper && wrapper.classList.contains('ts-wrapper')) {
      if (this.richValue) wrapper.classList.add('ts-rich')
      // Check if dark mode is active
      const isDarkMode = document.documentElement.classList.contains('dark')
      if (isDarkMode) {
        wrapper.classList.add('dark-mode')
      }
    }
  }

  // Allow external refresh (e.g., when options change dynamically)
  refresh() {
    if (this.tomSelect) {
      this.tomSelect.sync()
    }
  }

  // Clear selection
  clear() {
    if (this.tomSelect) {
      this.tomSelect.clear()
    }
  }
}
